import React, { useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { Banner } from './components/banner/Banner'
import { HeroFeature } from './components/heroFeature/HeroFeature'
import { Features } from './components/features/Features'
import { BannerVariant } from '../../common/types/enums'
import talks from '../../../src/assets/images/talks.webp'

import adminDesktopLight from '../../assets/images/mainNew/admin-desktop-light.webp'
import adminDesktopDark from '../../assets/images/mainNew/admin-desktop-dark.webp'
import adminMobileLight from '../../assets/images/mainNew/admin-mobile-light.webp'
import adminMobileDark from '../../assets/images/mainNew/admin-mobile-dark.webp'

import client1AppMobileLight from '../../assets/images/mainNew/client1-app-mobile-light.webp'
import client1AppMobileDark from '../../assets/images/mainNew/client1-app-mobile-dark.webp'
import client1SiteDesktopLight from '../../assets/images/mainNew/client1-site-desktop-light.webp'
import client1SiteDesktopDark from '../../assets/images/mainNew/client1-site-desktop-dark.webp'
import client1SiteMobileLight from '../../assets/images/mainNew/client1-site-mobile-light.webp'
import client1SiteMobileDark from '../../assets/images/mainNew/client1-site-mobile-dark.webp'

import client2AppMobileLight from '../../assets/images/mainNew/client2-app-mobile-light.webp'
import client2AppMobileDark from '../../assets/images/mainNew/client2-app-mobile-dark.webp'
import client2SiteDesktopLight from '../../assets/images/mainNew/client2-site-desktop-light.webp'
import client2SiteDesktopDark from '../../assets/images/mainNew/client2-site-desktop-dark.webp'
import client2SiteMobileLight from '../../assets/images/mainNew/client2-site-mobile-light.webp'
import client2SiteMobileDark from '../../assets/images/mainNew/client2-site-mobile-dark.webp'

import { useTheme } from '../../common/hooks/useTheme'
import styles from './MainNew.module.scss'
import { Audience } from './components/audience/Audience'
import { Timeline } from './components/timeline/Timeline'
import { NotAllowed } from './components/notAllowed/NotAllowed'
import { Pricing } from './components/pricing/Pricing'
import { WhyUs } from './components/whyUs/WhyUs'
import { FeaturesExtra } from './components/featuresExtra/FeaturesExtra'
import { LeadFeature } from './components/leadFeature/LeadFeature'
import { COUNTRY } from '../../common/utils/constants'

import {
  MdNotifications,
  MdChat,
  MdBarChart,
  MdRestaurant,
  MdWeb,
  MdPhoneAndroid,
  MdShoppingCart,
  MdInventory,
  MdStars,
  MdPayment,
  MdPeople,
  MdPrint,
} from 'react-icons/md'
import { Integrations } from './components/integrations/Integrations'
import { Cases } from './components/cases/Cases'
import { useMainNew } from './useMainNew'
import { Projects } from './components/projects/Projects'
import { Demos } from './components/demos/Demos'

const isRu = COUNTRY === 'ru'

const features = [
  { id: 'site', text: 'Сайт-витрина под ключ', icon: MdWeb },
  { id: 'app', text: 'Мобильное приложение', icon: MdPhoneAndroid },
  { id: 'orders', text: 'Приём заказов онлайн и офлайн', icon: MdShoppingCart },
  { id: 'stock', text: 'Склад и остатки', icon: MdInventory },
  { id: 'staff', text: 'Персонал и мотивация', icon: MdStars },
  ...(isRu ? [{ id: 'payments', text: 'Оплаты (СБП + карты)', icon: MdPayment }] : []),
  { id: 'crm', text: 'CRM и база клиентов', icon: MdPeople },
  { id: 'analytics', text: 'Аналитика и статистика', icon: MdBarChart },
  { id: 'notify', text: 'Уведомления клиентам', icon: MdNotifications },
  { id: 'chat', text: 'Чат с клиентами', icon: MdChat, future: true },
  { id: 'kassa', text: 'Касса + ОФД', icon: MdPrint },
  { id: 'table', text: 'Заказ за столом', icon: MdRestaurant },
]

const countryName: Record<string, string> = {
  ru: 'России',
  by: 'Беларуси',
  kz: 'Казахстане',
}
const title = `Операционная система для бизнеса в ${countryName[COUNTRY]}`

const bannerItems = [
  {
    id: 1,
    title,
    description:
      'Онлайн и офлайн-продажи, управление заказами, складом, персоналом и мотивацией — всё в одной платформе',
    image: '',
  },
]

const appImages = [
  { light: client1AppMobileLight, dark: client1AppMobileDark },
  { light: client2AppMobileLight, dark: client2AppMobileDark },
]

const siteImages = [
  {
    desktopLight: client1SiteDesktopLight,
    desktopDark: client1SiteDesktopDark,
    mobileLight: client1SiteMobileLight,
    mobileDark: client1SiteMobileDark,
  },
  {
    desktopLight: client2SiteDesktopLight,
    desktopDark: client2SiteDesktopDark,
    mobileLight: client2SiteMobileLight,
    mobileDark: client2SiteMobileDark,
  },
]

export const MainNew: React.FC = () => {
  const { theme } = useTheme()
  const { projects, demos } = useMainNew()

  const randomAppIndex = useMemo(() => Math.floor(Math.random() * appImages.length), [])
  const randomSiteIndex = useMemo(() => Math.floor(Math.random() * siteImages.length), [])

  const selectedApp = appImages[randomAppIndex]
  const selectedSite = siteImages[randomSiteIndex]

  const bannerWithImage = {
    ...bannerItems[0],
    image: theme === 'light' ? talks : talks,
  }

  const seoByCountry: Record<string, { title: string; description: string }> = {
    ru: {
      title: 'logid — платформа для вашего бизнеса',
      description: 'Сайт, мобильное приложение и CRM для вашего бизнеса за 2 дня',
    },
    by: {
      title: 'logid — платформа для вашего бизнеса в Беларуси',
      description: 'Сайт, мабільнае прыкладанне і CRM для вашага бізнесу за 2 дні',
    },
    kz: {
      title: 'logid — платформа для вашего бизнеса в Казахстане',
      description: 'Сайт, мобильное приложение и CRM для вашего бизнеса за 2 дня',
    },
  }

  const seo = seoByCountry[COUNTRY] || seoByCountry.ru

  return (
    <div className={styles.page}>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
      </Helmet>

      <section className={styles.bannerSection}>
        <Banner items={[bannerWithImage]} variant={BannerVariant.SPLIT} />
      </section>

      <section className={styles.section}>
        <div className={styles.container}>
          <HeroFeature
            reversed
            title="Мобильное приложение"
            description="Ваш личный канал продаж, который всегда с клиентом"
            image={theme === 'light' ? selectedApp.light : selectedApp.dark}
            bulletPoints={[
              'Всегда с клиентом',
              'Push-уведомления',
              'Бонусы и история',
              'Если клиент уже заходил - работает без интернета',
            ]}
          />
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.container}>
          <HeroFeature
            title="Сайт"
            description="Одинаково хорош на всех устройствах"
            desktopImage={theme === 'light' ? selectedSite.desktopLight : selectedSite.desktopDark}
            mobileImage={theme === 'light' ? selectedSite.mobileLight : selectedSite.mobileDark}
            bulletPoints={[
              'Адаптивный дизайн',
              'Быстрая загрузка',
              'SEO оптимизирован и подключен к аналитике, клиенты вас найдут',
              'Не отстает от нашего приложения в работе оффлайн',
            ]}
          />
        </div>
      </section>

      <section className={styles.section} id="features">
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Чем можно управлять</h2>
          <Features />
        </div>
      </section>

      <section className={styles.section} id="integrations">
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Интеграции</h2>
          <Integrations />
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.container}>
          <HeroFeature
            title="Единый кабинет управления"
            description="Полный контроль над бизнесом в одной панели"
            desktopImage={theme === 'light' ? adminDesktopLight : adminDesktopDark}
            mobileImage={theme === 'light' ? adminMobileLight : adminMobileDark}
            bulletPoints={[
              'Товары, цены, остатки',
              'Заказы и доставка',
              'Сотрудники и мотивация',
              'Аналитика и статистика',
            ]}
            reversed
          />
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Особенности</h2>
          <FeaturesExtra features={features} />
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.container}>
          <LeadFeature
            title="Есть вопросы?"
            description="Оставьте телефон и мы свяжемся с вами в ближайшее время!"
          />
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Для кого</h2>
          <Audience />
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.container}>
          <NotAllowed />
        </div>
      </section>

      <section id="cases">
        <Cases />
      </section>

      {projects.length > 0 && (
        <section className={styles.section} id="projects">
          <div className={styles.container}>
            <h2 className={styles.sectionTitle}>Примеры наших проектов</h2>
            <Projects items={projects} />
          </div>
        </section>
      )}

      {demos.length > 0 && (
        <section className={styles.section} id="demos">
          <div className={styles.container}>
            <h2 className={styles.sectionTitle}>Демо-проекты</h2>
            <Demos items={demos} />
          </div>
        </section>
      )}

      <section className={styles.section}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Скорость запуска</h2>
          <Timeline />
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Почему мы</h2>
          <WhyUs />
        </div>
      </section>

      <section className={styles.section} id="pricing">
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Стоимость</h2>
          <Pricing />
        </div>
        <div className={styles.pricingNote}>
          Разумная кастомизация под клиента, по согласованию. Подорожание не более 10% в год
        </div>
      </section>

      <section className={styles.section} id="request">
        <div className={styles.container}>
          <LeadFeature />
        </div>
      </section>
    </div>
  )
}